import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { eur } from "@/lib/api";
import WakeAlarm from "@/components/WakeAlarm";
import RepairReadyAlerts from "@/components/RepairReadyAlerts";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  House, ChartLineUp, Storefront, Package, Wrench, Clock, Receipt, Users, Bell, SignOut,
  ClockCounterClockwise, List, X, Archive, AddressBook, Warning, Calculator, ClipboardText, ArrowUUpLeft,
} from "@phosphor-icons/react";

const LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_pos-service-system/artifacts/xtczyvft_Logo2.png";

export default function Layout() {
  const { user, logout, isAdmin, can } = useAuth();
  const canNotif = can("notifiche");
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [overdue, setOverdue] = useState([]);
  const [showOverdue, setShowOverdue] = useState(false);
  const [overdueSosp, setOverdueSosp] = useState([]);
  const [showSosp, setShowSosp] = useState(false);

  useEffect(() => {
    const FIVE_DAYS = 5 * 24 * 60 * 60 * 1000;
    const last = Number(localStorage.getItem("overdue_shown_at") || 0);
    if (Date.now() - last > FIVE_DAYS) {
      api.get("/repairs/overdue").then((r) => {
        if (r.data.length > 0) {
          setOverdue(r.data);
          setShowOverdue(true);
          localStorage.setItem("overdue_shown_at", String(Date.now()));
        }
      }).catch(() => {});
    }
    if (!sessionStorage.getItem("sospesi_overdue_shown")) {
      api.get("/sales/pending-overdue").then((r) => {
        if (r.data.length > 0) {
          setOverdueSosp(r.data);
          sessionStorage.setItem("sospesi_overdue_shown", "1");
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (overdueSosp.length > 0 && !showOverdue) setShowSosp(true);
  }, [overdueSosp, showOverdue]);

  useEffect(() => {
    if (!canNotif) return;
    const load = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [canNotif]);

  const groups = [
    [
      { to: "/", label: "Home", icon: House, exact: true },
      { to: "/dashboard", label: "Dashboard", icon: ChartLineUp, perm: "dashboard" },
    ],
    [
      { to: "/banco", label: "Banco", icon: Storefront, perm: "banco" },
      { to: "/magazzino", label: "Magazzino", icon: Package, perm: "magazzino" },
      { to: "/movimenti-magazzino", label: "Movimenti giacenze", icon: ClockCounterClockwise, admin: true },
      { to: "/riparazioni", label: "Riparazioni", icon: Wrench, perm: "riparazioni" },
      { to: "/preventivi", label: "Preventivi", icon: ClipboardText, perm: "preventivi" },
      { to: "/archivio", label: "Acquisto Usato", icon: ClipboardText, perm: "archivio" },
      { to: "/resi", label: "Resi", icon: ArrowUUpLeft, perm: "resi" },
    ],
    [
      { to: "/chiusure", label: "Chiusure", icon: ClockCounterClockwise, perm: "chiusure" },
      { to: "/contabilita", label: "Contabilità", icon: Calculator, perm: "contabilita" },
      { to: "/sospesi", label: "Sospesi", icon: Clock, perm: "sospesi" },
      { to: "/sospesi-personali", label: "Sospesi Unificati", icon: Clock, perm: "sospesi_personali" },
      { to: "/spese", label: "Spese", icon: Receipt, perm: "spese" },
    ],
    [
      { to: "/archivio", label: "Archivio Documenti", icon: Archive, perm: "archivio" },
      { to: "/clienti", label: "Anagrafiche clienti", icon: AddressBook, perm: "clienti" },
    ],
    [
      { to: "/utenti", label: "Utenti", icon: Users, admin: true },
      { to: "/notifiche", label: "Notifiche", icon: Bell, perm: "notifiche", badge: unread },
    ],
  ];

  const close = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-background">
      <WakeAlarm />
      <RepairReadyAlerts />
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-card border-b border-border flex items-center justify-between px-4 z-40">
        <button onClick={() => setMobileOpen(true)} data-testid="menu-toggle" className="p-1.5 -ml-1.5 rounded-md hover:bg-secondary">
          <List size={24} weight="bold" />
        </button>
        <img src={LOGO} alt="UltraDigitale" className="h-7 w-auto object-contain" />
        <div className="w-8" />
      </div>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={close} />}

      <aside className={`w-64 shrink-0 border-r border-border bg-card flex flex-col fixed h-screen z-50 transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center justify-between gap-2 px-4 border-b border-border">
          <img src={LOGO} alt="UltraDigitale" className="h-9 w-auto object-contain" data-testid="app-logo" />
          <button onClick={close} className="lg:hidden p-1.5 rounded-md hover:bg-secondary"><X size={20} weight="bold" /></button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {groups.map((group, gi) => {
            const visible = group.filter((i) => (!i.admin || isAdmin) && (!i.perm || can(i.perm)));
            if (visible.length === 0) return null;
            return (
              <div key={gi} className={gi > 0 ? "pt-2 mt-2 border-t border-border/60" : ""}>
                {visible.map((i) => {
                  const Icon = i.icon;
                  return (
                    <NavLink
                      key={i.to}
                      to={i.to}
                      end={i.exact}
                      onClick={close}
                      data-testid={`nav-${i.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        }`
                      }
                    >
                      <Icon size={20} weight="bold" />
                      <span className="flex-1">{i.label}</span>
                      {i.badge > 0 && (
                        <span className="text-xs bg-[#F97316] text-white rounded-full px-1.5 py-0.5 font-mono">{i.badge}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-foreground truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role === "superadmin" ? "Titolare" : (user?.role_name || "Dipendente")}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-destructive hover:text-white transition-colors"
          >
            <SignOut size={18} weight="bold" /> Esci
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>

      <AlertDialog open={showOverdue} onOpenChange={setShowOverdue}>
        <AlertDialogContent data-testid="overdue-dialog" className="max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#F97316]">
              <Warning size={22} weight="fill" /> Riparazioni in ritardo
            </AlertDialogTitle>
            <AlertDialogDescription>
              {overdue.length} riparazion{overdue.length === 1 ? "e non è stata consegnata" : "i non sono state consegnate"} da più di 3 giorni.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {overdue.map((r) => (
              <div key={r.id} className="border border-border rounded-md p-2.5 text-sm flex items-center justify-between gap-2" data-testid="overdue-row">
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.customer_name} {r.customer_surname || ""} — {r.device_brand} {r.device_model || ""}</p>
                  <p className="text-xs text-muted-foreground">{r.number ? `#${r.number} · ` : ""}accettata {r.days_overdue} giorni fa</p>
                </div>
                <span className="text-xs font-semibold text-[#F97316] whitespace-nowrap">{r.days_overdue}g</span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="overdue-close">Chiudi</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/riparazioni")} data-testid="overdue-goto">Vai alle riparazioni</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSosp} onOpenChange={setShowSosp}>
        <AlertDialogContent data-testid="sospesi-overdue-dialog" className="max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#F97316]">
              <Warning size={22} weight="fill" /> Sospesi non saldati
            </AlertDialogTitle>
            <AlertDialogDescription>
              {overdueSosp.length} sospes{overdueSosp.length === 1 ? "o non è stato saldato" : "i non sono stati saldati"} da più di 7 giorni.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {overdueSosp.map((s) => (
              <div key={s.id} className="border border-border rounded-md p-2.5 text-sm flex items-center justify-between gap-2" data-testid="sospeso-overdue-row">
                <div className="min-w-0">
                  <p className="font-medium truncate">{s.label}</p>
                  <p className="text-xs text-muted-foreground">{s.type === "sale" ? "Vendita" : "Riparazione"} · da {s.days} giorni</p>
                </div>
                <span className="text-xs font-semibold text-[#F97316] whitespace-nowrap font-mono">{eur(s.remaining)}</span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sospesi-overdue-close">Chiudi</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/sospesi")} data-testid="sospesi-overdue-goto">Vai ai sospesi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
